import React, { useState, useEffect } from "react";
import {
  Plus,
  ChevronDown,
  BookOpen,
  Users,
  FileText,
  ClipboardList,
} from "lucide-react";
import UploadIcon from "../../Images/upload-cloud-01.svg";
import AssignmentModalPubliser from "./AssignmentPublisher";
import Edit_Icon from "../../Images/Edit_icon.svg";
import QuizModalPubliser from "./QuizPublisher";
import LabFormModal from "./Lab";
import Demo from "./Demo";
import { useLocation } from "react-router-dom";
import { API_URL } from "../../config/api";
import axios from "axios";

// Cloudinary configuration
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

export default function MLTDashboard() {
  const [showCourseInfo, setShowCourseInfo] = useState(false);
  const [courseTitle, setCourseTitle] = useState("");
  const [courseOverview, setCourseOverview] = useState("");
  const [courseThumbnail, setCourseThumbnail] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ type: "", message: "" });

  const location = useLocation();
  const courseId = location.state?.courseId;
  const schedule_id = location.state?.schedule_id;

  console.log("Schedule ID:", schedule_id);

  // Assignment Modal States
  const [showAssignmentModalPubliser, setShowAssignmentModalPubliser] =
    useState(false);
  const [showQuizModalPubliser, setShowQuizModalPubliser] = useState(false);
  const [showLabModal, setShowLabModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lessonToMark, setLessonToMark] = useState(null);
  const [dialogMessage, setDialogMessage] = useState("");

  const [modules, setModules] = useState([]);
  const [courseData, setCourseData] = useState(null);
  const [progressData, setProgressData] = useState(null);

  // ✅ CALCULATE QUIZ AND ASSIGNMENT COUNTS DYNAMICALLY
  const totalQuizzes = modules.reduce((count, module) => {
    return count + module.lessons.filter(lesson => 
      lesson.assessment_list?.includes("Quiz")
    ).length;
  }, 0);

  const totalAssignments = modules.reduce((count, module) => {
    return count + module.lessons.filter(lesson => 
      lesson.assessment_list?.includes("Assignment")
    ).length;
  }, 0);

  const stats = [
    {
      label: "Total Enrolled",
      value: courseData?.total_enrollments || "0",
      icon: Users,
    },
    {
      label: "Active Learners",
      value: courseData?.total_enrollments || "0",
      icon: Users,
    },
    {
      label: "Total Modules",
      value: courseData?.total_modules || "0",
      icon: BookOpen,
    },
    { 
      label: "Total Quiz Program", 
      value: totalQuizzes.toString(), // ✅ DYNAMIC COUNT
      icon: FileText 
    },
    { 
      label: "Total Assignments", 
      value: totalAssignments.toString(), // ✅ DYNAMIC COUNT
      icon: ClipboardList 
    },
  ];

  // Fetch course data from API
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) {
        setError("Course ID not provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/courses/${courseId}`);

        if (!response.ok) {
          throw new Error("Failed to fetch course data");
        }

        const result = await response.json();

        if (result.success && result.data) {
          const course = result.data;
          setCourseData(course);
          setCourseTitle(course.title || " ");
          setCourseOverview(course.description || "");
          setCourseThumbnail(course.program_thumbnail || null);

          // Transform API data to match component structure
          const transformedModules =
            course.modules?.map((module, moduleIndex) => ({
              id: module.module_id,
              displayId: moduleIndex + 1,
              title: module.title,
              progress: 0,
              learners: `${course.total_enrollments || 0}`,
              lessons:
                module.lessons?.map((lesson, lessonIndex) => ({
                  id: lesson.lesson_id,
                  displayId: lessonIndex + 1,
                  title: lesson.title,
                  type: lesson.lesson_type,
                  content: lesson.content,
                  assessment_list: lesson.assessment_list,
                  video_url: lesson.video_url,
                  duration_seconds: lesson.duration_seconds,
                  completed: false,
                })) || [],
            })) || [];

          setModules(transformedModules);
        } else {
          throw new Error(result.message || "Failed to load course data");
        }
      } catch (err) {
        console.error("Error fetching course:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId]);

  // Fetch progress data from API
  useEffect(() => {
    const fetchProgressData = async () => {
      if (!schedule_id) return;

      try {
        const response = await fetch(
          `${API_URL}/progress/progress/schedule/${schedule_id}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch progress data");
        }

        const result = await response.json();

        if (result.modules) {
          setProgressData(result.modules);

          // Update modules with progress data
          setModules((prevModules) =>
            prevModules.map((module) => {
              const progressModule = result.modules.find(
                (pm) => pm.module_id === module.id,
              );
              if (progressModule) {
                const progress =
                  progressModule.total_lessons > 0
                    ? Math.round(
                        (progressModule.completed_lessons /
                          progressModule.total_lessons) *
                          100,
                      )
                    : 0;

                return {
                  ...module,
                  progress,
                  learners: `${progressModule.completed_lessons}/${progressModule.total_lessons}`,
                  lessons: module.lessons.map((lesson) => ({
                    ...lesson,
                    completed: progressModule.lessons.completed.includes(
                      lesson.id,
                    ),
                  })),
                };
              }
              return module;
            }),
          );
        }
      } catch (err) {
        console.error("Error fetching progress:", err);
      }
    };

    fetchProgressData();
  }, [schedule_id, modules.length]); // Depend on modules.length to refetch after course data

  // Cloudinary upload handler
  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB for images)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setToast({
        type: "error",
        message: "File size exceeds 10MB limit",
      });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
      return;
    }

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      setToast({
        type: "error",
        message: "Only JPG, JPEG, and PNG formats are allowed",
      });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
      return;
    }

    setUploading(true);
    setToast({ type: "", message: "" });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        formData,
      );

      const url = response.data.secure_url;
      setCourseThumbnail(url);

      setToast({
        type: "success",
        message: "Thumbnail uploaded successfully!",
      });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
    } catch (error) {
      console.error("Upload error:", error);
      setToast({ type: "error", message: "Upload failed. Please try again." });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
    } finally {
      setUploading(false);
    }
  };

  // Save changes to API
  const handleSaveChanges = async () => {
    if (!courseId) {
      setToast({ type: "error", message: "Course ID not found" });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
      return;
    }

    setSaving(true);
    setToast({ type: "", message: "" });

    try {
      const payload = {
        title: courseTitle,
        description: courseOverview,
        program_thumbnail: courseThumbnail,
      };

      const response = await axios.patch(
        `${API_URL}/courses/${courseId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data) {
        setToast({
          type: "success",
          message: "Course updated successfully!",
        });
        setTimeout(() => setToast({ type: "", message: "" }), 3000);
      }
    } catch (error) {
      console.error("Save error:", error);
      setToast({
        type: "error",
        message: error.response?.data?.detail || "Failed to save changes",
      });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (parseInt(key) !== moduleId) {
          newState[key] = false;
        }
      });
      newState[moduleId] = !newState[moduleId];
      return newState;
    });
  };

  const handleMarkComplete = async (moduleId, lessonId) => {
    // Find the current lesson to determine its completion status
    const module = modules.find((m) => m.id === moduleId);
    const lesson = module?.lessons.find((l) => l.id === lessonId);
    const isCurrentlyCompleted = lesson?.completed;

    const newStatus = isCurrentlyCompleted ? "IN_PROGRESS" : "COMPLETED";

    try {
      const response = await axios.patch(
        `${API_URL}/progress/lesson/complete`,
        {
          schedule_id: schedule_id,
          module_id: moduleId,
          lesson_id: lessonId,
          status: newStatus,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data) {
        // Update modules immediately to reflect the change
        setModules((prevModules) =>
          prevModules.map((module) => {
            if (module.id === moduleId) {
              const lesson = module.lessons.find((l) => l.id === lessonId);
              if (lesson) {
                const wasCompleted = lesson.completed;
                const newCompleted = !wasCompleted;
                const currentCompleted = module.lessons.filter(
                  (l) => l.completed,
                ).length;
                const newCompletedCount = newCompleted
                  ? currentCompleted + 1
                  : currentCompleted - 1;
                const total = module.lessons.length;
                const newProgress =
                  total > 0 ? Math.round((newCompletedCount / total) * 100) : 0;
                return {
                  ...module,
                  progress: newProgress,
                  learners: `${newCompletedCount}/${total}`,
                  lessons: module.lessons.map((l) =>
                    l.id === lessonId ? { ...l, completed: newCompleted } : l,
                  ),
                };
              }
            }
            return module;
          }),
        );

        setToast({
          type: "success",
          message:
            newStatus === "COMPLETED"
              ? "Lesson marked as completed!"
              : "Lesson marked as in progress!",
        });
        setTimeout(() => setToast({ type: "", message: "" }), 3000);

        // Refetch progress data in background to ensure consistency
        const progressResponse = await fetch(
          `${API_URL}/progress/progress/schedule/${schedule_id}`,
        );
        if (progressResponse.ok) {
          const progressResult = await progressResponse.json();
          if (progressResult.modules) {
            setProgressData(progressResult.modules);

            // Update modules with latest progress data
            setModules((prevModules) =>
              prevModules.map((module) => {
                const progressModule = progressResult.modules.find(
                  (pm) => pm.module_id === module.id,
                );
                if (progressModule) {
                  const progress =
                    progressModule.total_lessons > 0
                      ? Math.round(
                          (progressModule.completed_lessons /
                            progressModule.total_lessons) *
                            100,
                        )
                      : 0;

                  return {
                    ...module,
                    progress,
                    learners: `${progressModule.completed_lessons}/${progressModule.total_lessons} `,
                    lessons: module.lessons.map((lesson) => ({
                      ...lesson,
                      completed: progressModule.lessons.completed.includes(
                        lesson.id,
                      ),
                    })),
                  };
                }
                return module;
              }),
            );
          }
        }
      }
    } catch (error) {
      console.error("Mark complete error:", error);
      setToast({
        type: "error",
        message: `Failed to mark lesson as ${newStatus.toLowerCase()}. Please try again.`,
      });
      setTimeout(() => setToast({ type: "", message: "" }), 3000);
    }
  };

  const handleOpenAssignment = (module, lesson) => {
    const payload = {
      courseTitle,
      moduleTitle: module.title,
      moduleDisplayId: module.displayId,
      moduleDbId: module.id,
      lessonTitle: lesson.title,
      lessonDisplayId: lesson.displayId,
      lessonDbId: lesson.id,
      schedule_id,
    };

    setSelectedLesson(payload);
    setShowAssignmentModalPubliser(true);
  };

  const handleOpenQuiz = (module, lesson) => {
    const payload = {
      courseTitle,
      moduleTitle: module.title,
      moduleDisplayId: module.displayId,
      moduleDbId: module.id,
      lessonTitle: lesson.title,
      lessonDisplayId: lesson.displayId,
      lessonDbId: lesson.id,
      schedule_id,
    };

    setSelectedLesson(payload);
    setShowQuizModalPubliser(true);
  };

  const handleCloseQuiz = () => {
    setShowQuizModalPubliser(false);
    setSelectedLesson(null);
    setSelectedAssessment(null);
  };

  const handleCloseAssignment = () => {
    setShowAssignmentModalPubliser(false);
    setSelectedLesson(null);
    setSelectedAssessment(null);
  };

  const handleOpenLab = (module, lesson) => {
    console.log("handleOpenLab called", module, lesson);
    const payload = {
      courseTitle,
      moduleTitle: module.title,
      moduleDisplayId: module.displayId,
      moduleDbId: module.id,
      lessonTitle: lesson.title,
      lessonDisplayId: lesson.displayId,
      lessonDbId: lesson.id,
      schedule_id,
    };

    setSelectedLesson(payload);
    setShowLabModal(true);
  };

  const handleCloseLab = () => {
    setShowLabModal(false);
    setSelectedLesson(null);
    setSelectedAssessment(null);
  };

  const handleOpenDemo = (module, lesson) => {
    console.log("handleOpenDemo called", module, lesson);
    const payload = {
      courseTitle,
      moduleTitle: module.title,
      moduleDisplayId: module.displayId,
      moduleDbId: module.id,
      lessonTitle: lesson.title,
      lessonDisplayId: lesson.displayId,
      lessonDbId: lesson.id,
      schedule_id,
    };

    setSelectedLesson(payload);
    setShowDemoModal(true);
  };

  const handleCloseDemo = () => {
    setShowDemoModal(false);
    setSelectedLesson(null);
    setSelectedAssessment(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4AACC5] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading course data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600">Error: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-[#4AACC5] text-white rounded hover:bg-[#62bcd3]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Toast Notification */}
      {toast.message && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-3 rounded shadow-lg ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h1 className="flex text-xl md:text-2xl font-bold text-zinc-800 md:mb-5">
          {courseTitle}
        </h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-white rounded shadow-sm p-4 md:p-6 flex flex-col items-center justify-center"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-cyan-100 rounded flex items-center justify-center mb-2 md:mb-3">
                  <Icon className="w-6 h-6 md:w-8 md:h-8 text-cyan-600" />
                </div>
                <div className="text-stone-900 text-xs md:text-sm font-medium text-center mb-1 md:mb-2">
                  {stat.label}
                </div>
                <div className="text-black text-lg md:text-2xl font-bold">
                  {stat.value}
                </div>
              </div>
            );
          })}
        </div>

        {/* Course Info Section */}
        <div className="mb-6">
          <div
            className="bg-white rounded shadow-sm border p-4 md:p-6 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setShowCourseInfo(!showCourseInfo)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 md:gap-5">
                <h2 className="text-lg md:text-xl font-semibold text-[#4AACC5]">
                  Program Info
                </h2>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-600 transition-transform ${
                  showCourseInfo ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>

          {showCourseInfo && (
            <div className="bg-white rounded shadow-sm border p-4 md:p-6 mt-3">
              <div className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Title
                  </label>
                  <input
                    type="text"
                    value={courseTitle}
                    onChange={(e) => setCourseTitle(e.target.value)}
                    placeholder="Medical Laboratory Technology"
                    className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm md:text-base"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Course Overview
                    </label>
                    <textarea
                      value={courseOverview}
                      onChange={(e) => setCourseOverview(e.target.value)}
                      rows="6"
                      className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent resize-none text-sm md:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Course Thumbnail
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded p-4 md:p-6 flex flex-col items-center justify-center h-[180px] hover:border-teal-400 transition-colors relative">
                      {uploading && (
                        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4AACC5]"></div>
                        </div>
                      )}
                      {courseThumbnail ? (
                        <div className="relative w-full h-full">
                          <img
                            src={courseThumbnail}
                            alt="Course thumbnail"
                            className="w-full h-full object-cover rounded"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCourseThumbnail(null);
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer text-center w-full h-full flex flex-col items-center justify-center">
                          <img src={UploadIcon} alt="" />
                          <span className="text-xs md:text-sm text-gray-500">
                            Upload Image
                          </span>
                          <span className="text-xs text-gray-400 mt-1">
                            (Max 10MB, Support: JPG, JPEG, PNG)
                          </span>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            onChange={handleThumbnailChange}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-start">
                  <button
                    onClick={handleSaveChanges}
                    disabled={saving || uploading}
                    className={`px-4 md:px-6 py-2 rounded transition-colors text-sm md:text-base font-bold ${
                      saving || uploading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-[#4AACC5] hover:bg-[#62bcd3] text-white"
                    }`}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modules List */}
        {modules.map((module) => (
          <div key={module.id} className="mb-4 md:mb-6">
            <div
              className="bg-white rounded shadow-sm border p-4 md:p-6 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleModule(module.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="flex flex-row items-start text-base md:text-xl font-semibold flex-1 mr-2">
                  <span className="text-[#4AACC5]">
                    Module:{module.displayId}{" "}
                  </span>
                  <span className="text-stone-900 ml-1">{module.title}</span>
                </h3>
                <div className="flex items-center gap-2">
                  <ChevronDown
                    className={`w-4 h-4 md:w-5 md:h-5 text-gray-600 transition-transform ${
                      expandedModules[module.id] ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </div>

              {module.lessons.length > 0 && (
                <div className="flex items-center gap-3 md:gap-4 mt-3 md:mt-4">
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-1.5 md:h-2">
                      <div
                        className="bg-[#4AACC5] h-1.5 md:h-2 rounded-full transition-all"
                        style={{ width: `${module.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-xs md:text-sm text-gray-600 font-medium">
                    {module.progress}%
                  </span>
                </div>
              )}
            </div>

            {expandedModules[module.id] && module.lessons.length > 0 && (
              <div className="bg-white rounded shadow-sm border mt-3 p-4 md:p-6">
                <div className="space-y-4">
                  {module.lessons.map((lesson) => (
                    <div
                      key={lesson.id}
                      className="border-b last:border-b-0 pb-4 last:pb-0"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 md:gap-3 flex-1 mr-2">
                          <span className="text-[#4AACC5] font-medium text-xs md:text-sm whitespace-nowrap">
                            Lesson {lesson.displayId}:
                          </span>
                          <span className="text-gray-700 font-medium text-sm md:text-base">
                            {lesson.title}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                        {Array.isArray(lesson.assessment_list) &&
                          lesson.assessment_list.map((assessment, idx) => {
                            return (
                              <button
                                key={idx}
                                onClick={() => {
                                  if (assessment === "Assignment") {
                                    handleOpenAssignment(module, lesson);
                                  }
                                  if (assessment === "Quiz") {
                                    handleOpenQuiz(module, lesson);
                                  }
                                  if (assessment === "Lab") {
                                    handleOpenLab(module, lesson);
                                  }
                                  if (assessment === "Demo") {
                                    handleOpenDemo(module, lesson);
                                  }
                                }}
                                className="px-3 md:px-4 py-1.5 border border-gray-300 rounded text-xs md:text-sm hover:bg-gray-50 flex items-center gap-1 md:gap-2 whitespace-nowrap"
                              >
                                <Plus className="w-3 h-3" />
                                {assessment}
                              </button>
                            );
                          })}

                        <button
                          onClick={() => {
                            setLessonToMark({
                              moduleId: module.id,
                              lessonId: lesson.id,
                            });
                            setDialogMessage(
                              lesson.completed
                                ? "Are you sure you want to mark this lesson as in progress?"
                                : "Are you sure you want to mark this lesson as completed?",
                            );
                            setShowConfirmDialog(true);
                          }}
                          className={`px-3 md:px-4 py-1.5 rounded text-xs md:text-sm font-medium flex items-center gap-1 md:gap-2 whitespace-nowrap ${
                            lesson.completed
                              ? "bg-green-500 text-white hover:bg-green-600"
                              : "border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {lesson.completed && (
                            <svg
                              className="w-3 h-3 md:w-4 md:h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          Mark As Completed
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="flex items-start w-full max-w-[513px] gap-2.5">
          <button
            className="flex-1 px-5 py-2.5 rounded outline outline-1 outline-[#4AACC5] 
               flex justify-center items-center gap-2 
               text-[#4AACC5] text-sm font-bold
               hover:bg-teal-50 transition"
          >
            <img
              src={Edit_Icon}
              alt="EditIcon"
              className="w-4 h-4 text-[#4AACC5]"
            />
            Edit Program
          </button>

          <button
            className="flex-1 px-5 py-2.5 rounded bg-[#4AACC5]
               flex justify-center items-center gap-2 
               text-white text-sm font-bold
               hover:bg-[#8acbdb] transition"
          >
            Preview Program
          </button>
        </div>
      </div>

      {/* Modals */}
      <AssignmentModalPubliser
        isOpen={showAssignmentModalPubliser}
        onClose={handleCloseAssignment}
        selectedLesson={selectedLesson}
        assessmentList={selectedAssessment}
      />
      <QuizModalPubliser
        isOpen={showQuizModalPubliser}
        onClose={handleCloseQuiz}
        selectedLesson={selectedLesson}
        assessmentList={selectedAssessment}
      />
      <LabFormModal
        isOpen={showLabModal}
        onClose={handleCloseLab}
        selectedLesson={selectedLesson}
      />
      <Demo
        isOpen={showDemoModal}
        onClose={handleCloseDemo}
        selectedLesson={selectedLesson}
      />

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Action
            </h3>
            <p className="text-gray-600 mb-6">{dialogMessage}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setLessonToMark(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (lessonToMark) {
                    handleMarkComplete(
                      lessonToMark.moduleId,
                      lessonToMark.lessonId,
                    );
                  }
                  setShowConfirmDialog(false);
                  setLessonToMark(null);
                }}
                className="px-4 py-2 bg-[#4AACC5] text-white rounded hover:bg-[#62bcd3] transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}